import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Extension as ExtensionIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Help as HelpIcon,
  LooksOne as OneIcon,
  LooksTwo as TwoIcon,
  Looks3 as ThreeIcon,
  Looks4 as FourIcon,
  Looks5 as FiveIcon,
  Looks6 as SixIcon,
} from "@mui/icons-material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import BoschCoreIntegrationCard from "./BoschCoreIntegrationCard";
import CabasIntegrationCard from "./CabasIntegrationCard";
import GoogleCalendarCard from "./GoogleCalendarCard";
import PaymentTerminalCard from "./PaymentTerminalCard";
import PasiIntegrationCard from "./PasiIntegrationCard";
import SupplierConnectionsSection from "./SupplierConnectionsSection";
import ResursFinancingCard from "./ResursFinancingCard";
import SveaFinancingCard from "./SveaFinancingCard";

import { can } from '../../utils/access';
function OrganizationIntegrationsTab({ t }) {
  const { currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;
  const [showToken, setShowToken] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [instructionsBrowser, setInstructionsBrowser] = useState(0); // 0 = Chrome, 1 = Firefox

  // Email import state
  const [partsInboxEmail, setPartsInboxEmail] = useState(null);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // Fetch organization data on mount
  useEffect(() => {
    if (organizationId) {
      fetchOrganizationData();
    }
  }, [organizationId]);

  const fetchOrganizationData = async () => {
    try {
      const response = await axios.get(`/api/organizations/${organizationId}/`);
      setPartsInboxEmail(response.data.parts_inbox_email);
      setAllowedEmails(response.data.allowed_parts_import_emails || []);
    } catch (error) {
      console.error('Failed to fetch organization data:', error);
    }
  };

  const handleCopyInboxEmail = () => {
    if (partsInboxEmail) {
      navigator.clipboard.writeText(partsInboxEmail);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleAddEmail = async () => {
    const emailToAdd = newEmail.trim().toLowerCase();

    if (!emailToAdd) {
      setEmailError(t("Email address is required"));
      return;
    }

    if (!validateEmail(emailToAdd)) {
      setEmailError(t("Invalid email address"));
      return;
    }

    if (allowedEmails.includes(emailToAdd)) {
      setEmailError(t("This email is already in the list"));
      return;
    }

    setEmailSaving(true);
    setEmailError("");

    try {
      const updatedEmails = [...allowedEmails, emailToAdd];
      await axios.patch(`/api/organizations/${organizationId}/`, {
        allowed_parts_import_emails: updatedEmails,
      });
      setAllowedEmails(updatedEmails);
      setNewEmail("");
    } catch (error) {
      console.error('Failed to add email:', error);
      setEmailError(t("Failed to add email"));
    } finally {
      setEmailSaving(false);
    }
  };

  const handleRemoveEmail = async (emailToRemove) => {
    setEmailSaving(true);

    try {
      const updatedEmails = allowedEmails.filter((e) => e !== emailToRemove);
      await axios.patch(`/api/organizations/${organizationId}/`, {
        allowed_parts_import_emails: updatedEmails,
      });
      setAllowedEmails(updatedEmails);
    } catch (error) {
      console.error('Failed to remove email:', error);
    } finally {
      setEmailSaving(false);
    }
  };

  const handleRevealToken = async () => {
    if (token) {
      setShowToken(!showToken);
      return;
    }

    if (!organizationId) {
      console.error('No organization ID available');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/users/api-token/?organization=${organizationId}`);
      setToken(response.data.token);
      setShowToken(true);
    } catch (error) {
      console.error('Failed to fetch API token:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadExtension = (browser = 'chrome') => {
    const link = document.createElement('a');
    if (browser === 'firefox') {
      link.href = '/verstaani-firefox-extension.zip';
      link.download = 'verstaani-firefox-extension.zip';
    } else {
      link.href = '/verstaani-extension.zip';
      link.download = 'verstaani-extension.zip';
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const chromeInstructions = [
    {
      icon: <OneIcon color="primary" />,
      text: t("Download the extension by clicking the download button below"),
    },
    {
      icon: <TwoIcon color="primary" />,
      text: t("Unzip the downloaded file to a folder on your computer"),
    },
    {
      icon: <ThreeIcon color="primary" />,
      text: t("Open Chrome and go to chrome://extensions"),
    },
    {
      icon: <FourIcon color="primary" />,
      text: t("Enable 'Developer mode' from the top right corner"),
    },
    {
      icon: <FiveIcon color="primary" />,
      text: t("Click 'Load unpacked' and select the unzipped extension folder"),
    },
    {
      icon: <SixIcon color="primary" />,
      text: t("Click the extension icon in Chrome toolbar, enter your API token, and start capturing parts!"),
    },
  ];

  const firefoxInstructions = [
    {
      icon: <OneIcon color="primary" />,
      text: t("Download the extension by clicking the download button below"),
    },
    {
      icon: <TwoIcon color="primary" />,
      text: t("Unzip the downloaded file to a folder on your computer"),
    },
    {
      icon: <ThreeIcon color="primary" />,
      text: t("Open Firefox and go to about:debugging#/runtime/this-firefox"),
    },
    {
      icon: <FourIcon color="primary" />,
      text: t("Click 'Load Temporary Add-on'"),
    },
    {
      icon: <FiveIcon color="primary" />,
      text: t("Select the manifest.json file from the unzipped extension folder"),
    },
    {
      icon: <SixIcon color="primary" />,
      text: t("Click the extension icon in Firefox toolbar, enter your API token, and start capturing parts!"),
    },
  ];

  const instructions = instructionsBrowser === 0 ? chromeInstructions : firefoxInstructions;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
          {t("Integrations")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Connect Verstaani with other services to enhance your workflow")}
        </Typography>
      </Box>

      {/* Integration cards grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
        <SupplierConnectionsSection />

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <ExtensionIcon />
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom>
              {t("Browser Extension")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("Capture parts from supplier websites with AI-powered extraction")}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadExtension('chrome')}
              >
                {t("Download Chrome Extension")}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadExtension('firefox')}
              >
                {t("Download Firefox Extension")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HelpIcon />}
                onClick={() => setInstructionsOpen(true)}
              >
                {t("Installation Instructions")}
              </Button>
            </Box>

            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRevealToken}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : (showToken ? <VisibilityOffIcon /> : <VisibilityIcon />)}
                sx={{ mb: showToken ? 1.5 : 0 }}
              >
                {loading ? <span>{t("Loading...")}</span> : (showToken ? <span>{t("Hide API Token")}</span> : <span>{t("Reveal API Token")}</span>)}
              </Button>

              {showToken && token && (
                <TextField
                  fullWidth
                  size="small"
                  value={token}
                  type="text"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '12px' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? t("Copied!") : t("Copy to clipboard")}>
                          <IconButton size="small" onClick={handleCopyToken}>
                            {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Google Calendar: beta only (weekly Google re-verification while the OAuth app is
            unverified), so orgs outside the beta never see it exists */}
        {currentOrganization?.google_calendar_beta_enabled && <GoogleCalendarCard />}

        {/* Payment Terminal Card: open to every workshop, adding a terminal switches the feature on */}
        {can(currentOrganization, "invoices.terminals") && <PaymentTerminalCard />}

        <SveaFinancingCard />

        {/* Resurs Bank financing: the store is linked by Verstaani staff, admins switch it on/off */}
        {can(currentOrganization, "settings.einvoicing") && <ResursFinancingCard />}

        {/* PASI / Mekonomen booking integration — admin only, gated to entitled orgs */}
        {currentOrganization?.pasi_integration_enabled &&
          can(currentOrganization, "settings.integrations") && <PasiIntegrationCard />}

        {/* Bosch Connected Repair (CoRe) — admin only, self-serve (no entitlement flag) */}
        {can(currentOrganization, "settings.integrations") && <BoschCoreIntegrationCard />}

        {/* CABAS repair estimates (CAB EstimateDataExtended): admin only, CABAS pilot orgs only
            (cabas_beta is set by Verstaani staff) */}
        {currentOrganization?.cabas_beta && can(currentOrganization, "settings.integrations") && (
          <CabasIntegrationCard />
        )}

        {/* Email Import Card */}
        <Card variant="outlined" sx={{ gridColumn: { md: "1 / -1" } }}>
          <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <EmailIcon />
            </Box>
          </Box>
          <Typography variant="h6" gutterBottom>
            {t("Email Import")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Import parts by forwarding emails to your organization's inbox. AI extracts part information automatically.")}
          </Typography>

          {/* Parts Inbox Email */}
          {partsInboxEmail && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("Your parts inbox")}
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={partsInboxEmail}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '13px' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={emailCopied ? t("Copied!") : t("Copy to clipboard")}>
                        <IconButton size="small" onClick={handleCopyInboxEmail}>
                          {emailCopied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {t("Forward invoices, quotes, or part lists from suppliers to this address")}
              </Typography>
            </Box>
          )}

          {/* Allowed Senders */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("Allowed senders")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("By default, only organization members can send emails. Add external email addresses (e.g., suppliers) that are allowed to send parts.")}
            </Typography>

            {/* Add new email */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder={t("supplier@example.com")}
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailError("");
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddEmail();
                  }
                }}
                error={!!emailError}
                helperText={emailError}
                sx={{ flex: 1 }}
                disabled={emailSaving}
              />
              <Button
                variant="outlined"
                onClick={handleAddEmail}
                disabled={emailSaving || !newEmail.trim()}
                startIcon={emailSaving ? <CircularProgress size={16} /> : <AddIcon />}
              >
                {t("Add")}
              </Button>
            </Box>

            {/* List of allowed emails */}
            {allowedEmails.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {allowedEmails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                    deleteIcon={<CloseIcon />}
                    size="small"
                    disabled={emailSaving}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                {t("No external senders added. Only organization members can send parts via email.")}
              </Alert>
            )}
          </Box>
        </CardContent>
        </Card>
      </Box>

      <Dialog
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ExtensionIcon color="primary" />
            {t("How to Install the Browser Extension")}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={instructionsBrowser}
            onChange={(e, v) => setInstructionsBrowser(v)}
            sx={{ mb: 2 }}
          >
            <Tab label={t("Chrome")} />
            <Tab label={t("Firefox")} />
          </Tabs>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {instructionsBrowser === 0
              ? t("Follow these steps to install the Verstaani browser extension in Chrome:")
              : t("Follow these steps to install the Verstaani browser extension in Firefox:")}
          </Typography>
          <List dense>
            {instructions.map((instruction, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {instruction.icon}
                </ListItemIcon>
                <ListItemText
                  primary={instruction.text}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {instructionsBrowser === 0 ? (
                <><strong>{t("Tip:")}</strong> {t("After installation, pin the extension to your toolbar for quick access. Click the puzzle icon in Chrome and pin Verstaani.")}</>
              ) : (
                <><strong>{t("Note:")}</strong> {t("Temporary add-ons are removed when Firefox restarts. For permanent installation, the extension must be signed via addons.mozilla.org.")}</>
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDownloadExtension(instructionsBrowser === 0 ? 'chrome' : 'firefox')} startIcon={<DownloadIcon />}>
            {instructionsBrowser === 0 ? t("Download Chrome Extension") : t("Download Firefox Extension")}
          </Button>
          <Button onClick={() => setInstructionsOpen(false)} variant="contained">
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default withTranslation()(OrganizationIntegrationsTab);
